import { useMemo, useState } from "react";
import { useDataProvider, useGetList, useNotify, useRefresh } from "ra-core";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  AlertCircle,
  Bell,
  Check,
  Download,
  Search,
  ShieldAlert,
} from "lucide-react";
import { PageHeader } from "../common/PageHeader";
import type { CrmDataProvider } from "../../providers/types";

type AgingRow = {
  id: number;
  customer_id: number;
  customer_name: string;
  total_outstanding: number;
  bucket_0_30: number;
  bucket_31_60: number;
  bucket_61_90: number;
  bucket_90_plus: number;
  oldest_due_days: number | null;
  open_invoice_count: number;
  credit_limit: number | null;
  breach_flag: boolean;
};

type ContactEmailRow = {
  id: number;
  company_id: number | null;
  email_jsonb: { email: string; type: string }[] | null;
};

const fmtINR = (n: number | null | undefined) =>
  `₹${(Number(n ?? 0)).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}`;

const riskLabel = (days: number | null): "High" | "Medium" | "Low" => {
  if ((days ?? 0) >= 60) return "High";
  if ((days ?? 0) >= 30) return "Medium";
  return "Low";
};

export const CustomerAgingPage = () => {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const notify = useNotify();
  const refresh = useRefresh();
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<number[]>([]);
  const [busyIds, setBusyIds] = useState<number[]>([]);
  const [reminded, setReminded] = useState<number[]>([]);

  const { data, isLoading } = useGetList<AgingRow>(
    "customer_aging_view",
    {
      pagination: { page: 1, perPage: 200 },
      sort: { field: "total_outstanding", order: "DESC" },
    },
  );

  const rows = useMemo(
    () =>
      (data ?? []).filter((row) =>
        search
          ? row.customer_name.toLowerCase().includes(search.toLowerCase())
          : true,
      ),
    [data, search],
  );

  const customerIds = useMemo(
    () => (data ?? []).map((row) => row.customer_id),
    [data],
  );
  // Emails live on contacts (email_jsonb), not on companies — there's no
  // companies.email column.
  const { data: customerContacts } = useGetList<ContactEmailRow>(
    "contacts",
    {
      pagination: { page: 1, perPage: 200 },
      filter: customerIds.length
        ? { "company_id@in": `(${customerIds.join(",")})` }
        : {},
    },
    { enabled: customerIds.length > 0 },
  );
  const emailLookup = useMemo(() => {
    const map = new Map<number, string>();
    (customerContacts ?? []).forEach((row) => {
      if (row.company_id == null || map.has(row.company_id)) return;
      const email = row.email_jsonb?.[0]?.email;
      if (email) map.set(row.company_id, email);
    });
    return map;
  }, [customerContacts]);

  const totals = useMemo(() => {
    const sum = (key: keyof AgingRow) =>
      (data ?? []).reduce((acc, row) => acc + Number(row[key] ?? 0), 0);
    return {
      consolidated: sum("total_outstanding"),
      healthy: sum("bucket_0_30"),
      critical: sum("bucket_90_plus"),
    };
  }, [data]);

  const toggleAll = () => {
    setSelected(
      selected.length === rows.length ? [] : rows.map((r) => r.customer_id),
    );
  };
  const toggleOne = (id: number) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const sendReminder = async (row: AgingRow) => {
    const toEmail = emailLookup.get(row.customer_id);
    if (!toEmail) {
      notify(
        `No email on file for ${row.customer_name}. Add one in Companies → ${row.customer_name}.`,
        { type: "warning" },
      );
      return;
    }

    setBusyIds((p) => [...p, row.customer_id]);
    try {
      const slug =
        (row.oldest_due_days ?? 0) >= 60
          ? "aging_reminder_60d"
          : "aging_reminder_30d";
      await dataProvider.sendTemplatedEmail({
        templateSlug: slug,
        toEmail,
        category: "customer",
        payload: {
          customer_name: row.customer_name,
          invoice_reference: "(consolidated)",
          amount: row.total_outstanding.toFixed(2),
          due_date: "",
          invoice_date: "",
          days_overdue: String(row.oldest_due_days ?? 0),
        },
      });
      // Mark this customer's open invoices as reminded so the nightly
      // fn_enqueue_aging_reminders cron (which uses the same 7-day
      // suppression window) doesn't email them again right after a
      // manual send.
      try {
        const { data: openInvoices } = await dataProvider.getList(
          "customer_invoices",
          {
            pagination: { page: 1, perPage: 200 },
            sort: { field: "id", order: "ASC" },
            filter: {
              "company_id@eq": row.customer_id,
              "payment_status@neq": "paid",
            },
          },
        );
        const ids = (openInvoices ?? []).map((inv: any) => inv.id);
        if (ids.length > 0) {
          await dataProvider.updateMany("customer_invoices", {
            ids,
            data: { reminder_sent_at: new Date().toISOString() },
          });
        }
      } catch (syncError) {
        console.error("Failed to sync reminder_sent_at:", syncError);
      }

      notify(`Reminder queued for ${row.customer_name}`, { type: "success" });
      setReminded((p) => [...p, row.customer_id]);
      refresh();
    } catch (error) {
      console.error(error);
      notify(
        `Failed to send reminder: ${(error as Error).message}`,
        { type: "error" },
      );
    } finally {
      setBusyIds((p) => p.filter((x) => x !== row.customer_id));
    }
  };

  const sendBulk = async () => {
    for (const id of selected) {
      const row = rows.find((r) => r.customer_id === id);
      if (row) await sendReminder(row);
    }
    setSelected([]);
  };

  return (
    <div className="p-6 bg-muted/30 min-h-screen">
      <div className="max-w-7xl mx-auto">
      <PageHeader
        title="Customer Aging & Alerts"
        description="Real-time aging dashboard monitoring collection health and triggers."
      />

      <Card className="mb-6 border-0 shadow-lg bg-slate-900 text-white overflow-hidden">
        <CardContent className="p-6 flex flex-col md:flex-row gap-10 justify-between items-center">
          <div>
            <p className="text-muted-foreground text-[10px] font-extrabold uppercase tracking-widest">
              Consolidated Receivables
            </p>
            <h2 className="text-4xl font-black mt-1.5 tracking-tight">
              {fmtINR(totals.consolidated)}
            </h2>
            <div className="flex items-center gap-2 text-xs text-emerald-400 font-bold mt-2 bg-slate-800 w-fit px-2 py-1 rounded">
              <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse"></div>
              Live from customer_invoices
            </div>
          </div>
          <div className="flex gap-4 flex-1 justify-end max-w-lg w-full">
            <div className="flex-1 space-y-2">
              <div className="flex justify-between text-xs font-bold">
                <span className="text-slate-300">Healthy (0-30 Days)</span>
                <span className="text-white">{fmtINR(totals.healthy)}</span>
              </div>
              <div className="w-full bg-slate-700 rounded-full h-1.5 overflow-hidden">
                <div
                  className="h-full bg-emerald-500"
                  style={{
                    width:
                      totals.consolidated > 0
                        ? `${Math.min(100, (totals.healthy / totals.consolidated) * 100)}%`
                        : "0%",
                  }}
                />
              </div>
            </div>
            <div className="flex-1 space-y-2 bg-slate-800/50 p-3 rounded-lg border border-slate-700">
              <div className="flex justify-between text-xs font-bold">
                <span className="text-rose-400">Critical (90+ Days)</span>
                <span className="text-rose-400">{fmtINR(totals.critical)}</span>
              </div>
              <div className="w-full bg-slate-700 rounded-full h-1.5 overflow-hidden">
                <div
                  className="h-full bg-rose-500 shadow-glow"
                  style={{
                    width:
                      totals.consolidated > 0
                        ? `${Math.min(100, (totals.critical / totals.consolidated) * 100)}%`
                        : "0%",
                  }}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-none border border-border bg-card overflow-hidden mb-6">
        <CardHeader className="border-b bg-muted/50 flex flex-row items-center justify-between py-4">
          <CardTitle className="text-base font-bold flex items-center gap-2 text-foreground">
            <AlertCircle className="text-red-500 h-5 w-5" /> Overdue Account Registry
          </CardTitle>
          <div className="flex gap-2 items-center">
            <div className="relative max-w-xs hidden md:block">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Find customer..."
                className="pl-8 h-8 bg-card border-input text-xs shadow-none w-[160px]"
              />
            </div>
            {selected.length > 0 && (
              <Button
                size="sm"
                onClick={sendBulk}
                className="bg-red-600 hover:bg-red-700 text-white font-bold shadow-md text-xs h-8 gap-1.5 animate-in zoom-in-95"
              >
                <Bell className="w-3.5 h-3.5" /> Send {selected.length} reminders
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              className="border-input h-8 font-bold text-xs text-muted-foreground"
            >
              <Download className="w-3.5 h-3.5 mr-1.5" /> Export
            </Button>
          </div>
        </CardHeader>

        <div className="w-full overflow-x-auto border border-border rounded-xl overflow-hidden">
          <table className="w-full border-collapse text-left">
            <thead>
              <tr className="bg-muted border-b-2 border-border">
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-muted-foreground border-r border-border w-10">
                  <Checkbox
                    checked={
                      rows.length > 0 && selected.length === rows.length
                    }
                    onCheckedChange={toggleAll}
                  />
                </th>
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-muted-foreground border-r border-border">
                  Customer Account
                </th>
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-muted-foreground border-r border-border">
                  Outstanding Sum
                </th>
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-muted-foreground border-r border-border">
                  Peak Age (Days)
                </th>
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-muted-foreground border-r border-border">
                  Risk Exposure
                </th>
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-muted-foreground text-right">
                  Financial Controls
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-12 text-center text-sm text-muted-foreground"
                  >
                    Loading aging data…
                  </td>
                </tr>
              )}
              {!isLoading && rows.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-12 text-center text-sm text-muted-foreground"
                  >
                    No outstanding receivables. All clear.
                  </td>
                </tr>
              )}
              {rows.map((row) => {
                const wasReminded = reminded.includes(row.customer_id);
                const isBusy = busyIds.includes(row.customer_id);
                const risk = riskLabel(row.oldest_due_days);
                return (
                  <tr
                    key={row.customer_id}
                    className={`${selected.includes(row.customer_id) ? "bg-blue-50/20" : "even:bg-muted/50 hover:bg-muted/50"} border-b border-border transition-all`}
                  >
                    <td className="px-4 py-3.5 text-sm border-r border-border align-middle">
                      <Checkbox
                        checked={selected.includes(row.customer_id)}
                        onCheckedChange={() => toggleOne(row.customer_id)}
                      />
                    </td>
                    <td className="px-4 py-3.5 text-sm border-r border-border font-bold text-foreground align-middle">
                      {row.customer_name}
                      {row.breach_flag && (
                        <Badge className="ml-2 bg-rose-50 text-rose-700 border-rose-200 text-[10px] font-bold">
                          OVER LIMIT
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-3.5 text-sm border-r border-border font-black text-foreground align-middle">
                      {fmtINR(row.total_outstanding)}
                    </td>
                    <td className="px-4 py-3.5 text-sm border-r border-border align-middle">
                      <span className="text-rose-600 font-black bg-rose-50 px-2 py-1 rounded border border-rose-100 text-xs">
                        {row.oldest_due_days ?? 0} Days Overdue
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-sm border-r border-border align-middle">
                      <Badge
                        className={`${risk === "High" ? "bg-rose-100 text-rose-700 border-rose-200" : risk === "Medium" ? "bg-amber-100 text-amber-700 border-amber-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"} font-extrabold shadow-none border`}
                      >
                        {risk} SEVERITY
                      </Badge>
                    </td>
                    <td className="px-4 py-3.5 text-sm text-right align-middle">
                      {wasReminded ? (
                        <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 font-bold shadow-none gap-1 animate-in slide-in-from-right-2">
                          <Check className="w-3 h-3" /> REMINDER SENT
                        </Badge>
                      ) : (
                        <Button
                          onClick={() => sendReminder(row)}
                          disabled={isBusy}
                          size="sm"
                          className="h-8 text-xs font-bold bg-slate-900 hover:bg-red-600 text-white shadow-sm gap-1.5 transition-all"
                        >
                          <ShieldAlert className="w-3.5 h-3.5" />
                          {isBusy ? "Sending…" : "Send Reminder"}
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
      </div>
    </div>
  );
};
